"use client";

import Image from "next/image";
import Link from "next/link";
import { useTranslations } from 'next-intl';
import { montserratMedium, montserratExtraBold, montserratSemi, montserratRegular } from "../../fonts";
import { useState, useMemo, memo, useEffect } from "react";
import TextType from '../../components/text-type';
import CountdownTimer from "../../components/countdown-timer";
import XBackground from '../../components/x-background';

const socialLinks = [
  { name: "X", icon: "fa-brands fa-x-twitter" },
  { name: "Instagram", icon: "fa-brands fa-instagram" },
  { name: "LinkedIn", icon: "fa-brands fa-linkedin-in" },
];

const takeOptions = [
  {
    id: 1,
    title: "Up to 100<span style='color: var(--main-color)'>X</span> Leverage",
    content: "Content for option 1 - This could be an image, text, or video",
    type: "image",
    icon: "/icons/100x.svg",
    image: "/assets/trading-platform.png",
  },
  {
    id: 2,
    title: "<span style='color: var(--main-color)'>O</span>n-Chain <br/> Self-Custodial",
    content: "Content for option 2 - This could be an image, text, or video",
    type: "image",
    icon: "/icons/on-chain.svg",
    image: "/assets/trading-platform.png",
  },
  {
    id: 3,
    title: "<span style='color: var(--main-color)'>X</span>-Tra Deep Liquidity",
    content: "Content for option 3 - This could be an image, text, or video",
    type: "image",
    icon: "/icons/x-tra.svg",
    image: "/assets/trading-platform.png",
  },
];

const featureCards = [
  {
    title: "Secure \n Settlement Layer",
    description: "Enjoy self-custody and enhanced security with low fees.",
    icon: "/icons/tech-1.svg",
    hoverIcon: "/icons/tech-1-hover.svg",
  },
  {
    title: "Lightning-Fast Match Engine",
    description: "Trade efficiently with 200,000 orders per second and 100x leverage.",
    icon: "/icons/tech-2.svg",
    hoverIcon: "/icons/tech-2-hover.svg",
  },
  {
    title: "Hybrid Liquidity Access",
    description: "Experience seamless cross-chain trading, deposits and withdrawals.",
    icon: "/icons/tech-3.svg",
    hoverIcon: "/icons/tech-3-hover.svg",
  },
  {
    title: "Unified DeFi Interface",
    description: "Navigate easily with a user-friendly platform for mobile and desktop.",
    icon: "/icons/tech-4.svg",
    hoverIcon: "/icons/tech-4-hover.svg",
  },
];

const Home = memo(function Home() {
  const t = useTranslations('maintenance');
  const [activeTakeOption, setActiveTakeOption] = useState(takeOptions[0]);
  const [isHeadlineFirstDone, setIsHeadlineFirstDone] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [showHeroLoader, setShowHeroLoader] = useState(true);
  const [secondLineText, setSecondLineText] = useState("");
  const [secondLineTyping, setSecondLineTyping] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setHeroLoaded(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (!heroLoaded) return;
    const timer = setTimeout(() => setShowHeroLoader(false), 1000);
    return () => clearTimeout(timer);
  }, [heroLoaded]);

  useEffect(() => {
    if (!isHeadlineFirstDone) {
      setSecondLineText("");
      setSecondLineTyping(false);
      return;
    }

    const target = t('launchDate');
    let index = 0;
    setSecondLineTyping(true);

    const interval = setInterval(() => {
      index += 1;
      setSecondLineText(target.slice(0, index));
      if (index >= target.length) {
        clearInterval(interval);
        setSecondLineTyping(false);
      }
    }, 75);

    return () => clearInterval(interval);
  }, [isHeadlineFirstDone]);

  const coinSymbols = useMemo(() => ["BITSTAMP:BTCUSD", "BITSTAMP:ETHUSD", "CRYPTO:SOLUSD"], []);
  
  const heroTerminal = useMemo(
    () => (
        <div id="x-background" style={{ width: '100%', height: '100%', position: 'relative' }}>
          <XBackground />
        </div>
      ),
    [],
  );

  return (
    <div className="relative min-h-screen overflow-hidden bg-[color:var(--background)] text-[color:var(--text-primary)]"> 

      <main className="relative z-10 flex flex-col">
        {/* Home Banner */}
        <section
          id="home-banner"
          className="relative min-h-screen flex items-center justify-center overflow-hidden px-6 py-24 sm:px-12"
        >
          <div
            className={`absolute inset-0 z-200 flex items-center justify-center bg-black transition-opacity duration-700 ${
              showHeroLoader ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <img src="/icons/loading.svg" alt={t('loadingAlt')} className="h-16 w-16" />
          </div>
          <div className="absolute inset-0 z-10">{heroTerminal}</div>
          <div className="relative z-10 mx-0 flex max-w-6xl flex-col items-center text-center" onLoad={() => setHeroLoaded(true)}>
            <div className="flex items-center gap-8 pb-3">
              <Image
                src="/assets/logo-w-spin.svg"
                alt={t('logoAlt')}
                width={164}
                height={48}
                className="h-10 w-auto md:h-12"
                priority
              />
            </div>
            <span className={`${montserratMedium.className} text-sm uppercase tracking-wider text-[color:var(--text-muted)]`}>
              {t('comingSoon')}
            </span>
            <h1 className={`${montserratExtraBold.className} md:text-[3.5rem] text-[1.5rem] pt-6 mt-6 md:pt-0 md:mt-0`}>
              <TextType
                as="span"
                text={t('title')}
                typingSpeed={75}
                pauseDuration={1500}
                loop={false}
                showCursor={!isHeadlineFirstDone}
                cursorCharacter="_"
                className="block bg-gradient-to-r from-[#0B71FF] via-white to-white bg-clip-text text-transparent"
                startOnVisible
                onSentenceComplete={() => setIsHeadlineFirstDone(true)}
              />
              <br />
              <span className={`block text-white transition-opacity duration-300 ${isHeadlineFirstDone ? "opacity-100" : "opacity-0"}`}>
                {secondLineText}
                <span className={`inline-block w-1 ${secondLineTyping ? "animate-pulse" : "opacity-0"}`}>_</span>
              </span>
            </h1>
            <CountdownTimer targetDate="2026-02-28T00:00:00Z" />
          </div>
          {/* Back Button */}
          <Link 
            href="/"
            className={`${montserratSemi.className} fixed top-6 left-6 z-50 flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--background)] border-2 border-[color:var(--text-primary)] text-[color:var(--text-primary)] hover:bg-[color:var(--text-primary)] hover:text-[color:var(--background)] transition-all duration-300 hover:shadow-[0_15px_30px_rgba(255,255,255,0.35)]`}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="rotate-180"
            >
              <path 
                d="M6 12L10 8L6 4" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            <span>{t('backToHome')}</span>
          </Link>
        </section>
      </main>
    </div>
  );
});

export default Home;
